import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Text } from "../components/catalyst/text";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "../components/ui/card";
import { Header } from "../layout/Header";
import { Calendar, Route, MapPin, Bookmark, UserCheck, UserX } from "lucide-react";
import { ScrollArea } from "../components/ui/scroll-area";
import { format } from "date-fns";
import useSavedSchedules from "../hooks/useSavedSchedules";
import MapSection from "../components/MapSection";
import AllSchedulesMapSection from "../components/AllSchedulesMapSection";
import SaveScheduleButton from "../components/SaveScheduleButton";
import { Progress } from "../components/ui/progress";
import { formatDate } from "../utils/dateUtils";
import AssignRsmModal from "../components/AssignRsmModal";

// Progress status colors
const statusColors = {
  PENDING: "bg-muted",
  IN_PROGRESS: "bg-blue-500",
  COMPLETED: "bg-green-500",
  CANCELLED: "bg-red-500",
  RESCHEDULED: "bg-amber-500"
};

function SavedSchedulesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    savedSchedules,
    loading,
    error,
    selectedScheduleId,
    selectedSchedule,
    setSelectedScheduleId,
    fetchSavedSchedules,
    toggleSaveSchedule
  } = useSavedSchedules();
  const [assignModalOpen, setAssignModalOpen] = useState(false);

  
  // Debug output
  console.log("SavedSchedulesPage render - User:", user?.username);
  console.log("SavedSchedulesPage - Saved Schedules:", savedSchedules?.length);
  console.log("SavedSchedulesPage - Loading:", loading);
  console.log("SavedSchedulesPage - Error:", error);

  const handleScheduleClick = (scheduleId) => {
    setSelectedScheduleId(scheduleId);
  };

  const handleViewDetails = () => {
    if (selectedScheduleId) {
      navigate(`/schedules/${selectedScheduleId}`);
    }
  };

  const getPriorityBadgeVariant = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return "destructive";
      case "medium":
        return "secondary";
      case "low":
        return "default";
      default:
        return "outline";
    }
  };

  // Calculate progress for the selected schedule
  const calculateProgress = (schedule) => {
    if (!schedule?.itinerary?.customers || schedule.itinerary.customers.length === 0) {
      return 0;
    }

    const customers = schedule.itinerary.customers;
    const completed = customers.filter(c => c.status === "COMPLETED").length;
    const inProgress = customers.filter(c => c.status === "IN_PROGRESS").length;
    
    return Math.round((completed + (inProgress * 0.5)) / customers.length * 100);
  };


  // Create directionsRequest for route optimization similar to BuildItineraryPage
  const directionsRequest = useMemo(() => {
    if (!selectedSchedule?.itinerary?.customers) return null;
    
    const customers = selectedSchedule.itinerary.customers;
    if (customers.length < 1) return null;

    const waypoints = customers
      .map((c) => ({ location: c.location }))
      .filter((wp) => wp.location);

    if (waypoints.length < 1) return null;

    // Create optimal route connecting all customer stops in sequence
    if (waypoints.length === 1) {
      return null; // Single customer - no route needed
    } else if (waypoints.length === 2) {
      return {
        origin: waypoints[0],
        destination: waypoints[1],
        waypoints: [],
        optimizeWaypoints: false,
        travelMode: "DRIVING",
        routeType: "simple"
      };
    } else {
      return {
        origin: waypoints[0],
        destination: waypoints[waypoints.length - 1], 
        waypoints: waypoints.slice(1, -1),
        optimizeWaypoints: true,
        travelMode: "DRIVING",
        routeType: "optimized"
      };
    }
  }, [selectedSchedule]);

  // Add handler for Google Maps
  const handleOpenGoogleMaps = () => {
    if (!selectedSchedule?.itinerary?.customers?.length) return;
    const customers = selectedSchedule.itinerary.customers;
    const waypoints = customers.map(c => c.location).filter(Boolean);
    if (!waypoints.length) return;
    const origin = encodeURIComponent(waypoints[0]);
    const destination = encodeURIComponent(waypoints[waypoints.length - 1]);
    const stops = waypoints.slice(1, -1).map(encodeURIComponent).join("|");
    let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}`;
    if (stops) url += `&waypoints=${stops}`;
    window.open(url, "_blank");
  };

  // If there's a loading error, display it
  if (error && !loading) {
    return (
      <div>
        <Header />
        <div className="p-8 text-center">
          <div className="mb-4 text-xl font-semibold">Error Loading Saved Schedules</div>
          <div className="text-red-500 mb-4">{error}</div>
          <Button 
            onClick={() => window.location.reload()}
            variant="outline"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Saved Schedules</h1>
        </div>

        {/* All Schedules Overview Map */}
        {savedSchedules && savedSchedules.length > 0 && (
          <Card className="overflow-hidden h-[400px] mb-6">
            <CardHeader className="p-4 border-b">
              <CardTitle className="text-lg font-semibold">
                All Saved Schedules Overview Map
              </CardTitle>
              <CardDescription>
                Showing all customer stops across {savedSchedules.length} saved schedules
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0 h-full">
              <AllSchedulesMapSection schedules={savedSchedules} />
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <Card className="flex flex-col h-full">
              <CardHeader className="flex flex-row justify-between items-center p-4 border-b">
                <CardTitle className="text-base font-semibold">
                  My Saved Schedules ({savedSchedules?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 flex-1 overflow-hidden">
                <ScrollArea className="h-[calc(100vh-220px)]">
                  <div className="divide-y">
                    {loading ? (
                      <div className="p-4 text-center">Loading schedules...</div>
                    ) : error ? (
                      <div className="p-4 text-center text-red-500">{error}</div>
                    ) : savedSchedules && savedSchedules.length > 0 ? (
                      savedSchedules.map((schedule) => {
                        const progress = calculateProgress(schedule);
                        
                        return (
                          <div
                            key={schedule.cluster}
                            className={`px-4 py-3 cursor-pointer transition-colors hover:bg-muted ${
                              selectedScheduleId === schedule.cluster
                                ? "bg-muted"
                                : ""
                            }`}
                            onClick={() => handleScheduleClick(schedule.cluster)}
                          >
                            <div className="flex justify-between items-start gap-2 mb-2">
                              <div>
                                <div className="font-medium">
                                  {schedule.metadata?.itinerary_name ||
                                    `Schedule ${schedule.cluster}`}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                  Saved: {formatDate(schedule.updated_at || schedule.created_at)}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1 flex gap-2 flex-wrap">
                                  <span>Region: {schedule.metadata?.region || "N/A"}</span>
                                  <span>Stops: {schedule.itinerary?.customers?.length || 0}</span>
                                  {schedule.assigned_to && (
                                    <span className="flex items-center gap-1">
                                      <UserCheck className="h-3 w-3 text-green-600" />
                                      {schedule.assigned_to.full_name}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <Badge
                                variant={getPriorityBadgeVariant(schedule.priority)}
                                className="uppercase text-[10px]"
                              >
                                {schedule.priority || "N/A"}
                              </Badge>
                            </div>
                            
                            <div className="space-y-1">
                              <div className="flex justify-between text-xs text-muted-foreground">
                                <span>Progress</span>
                                <span>{progress}%</span>
                              </div>
                              <Progress value={progress} className="h-2" />
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="p-4 text-center text-muted-foreground">
                        You don't have any saved schedules.
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-2">
            {selectedSchedule ? (
              <div className="space-y-4">
                {/* Map Card */}
                <Card className="overflow-hidden h-[300px]">
                  <CardContent className="p-0 h-full relative">
                    <MapSection
                      customers={selectedSchedule.itinerary?.customers || []}
                      directionsRequest={directionsRequest}
                    />
                  </CardContent>
                </Card>
                
                {/* Schedule Info & Progress */}
                <Card>
                  <CardHeader className="flex flex-row justify-between items-center p-4 border-b">
                    <div>
                      <CardTitle className="text-base font-semibold">
                        {selectedSchedule.metadata?.itinerary_name ||
                          `Schedule ${selectedSchedule.cluster}`}
                      </CardTitle>
                      <CardDescription>
                        {selectedSchedule.itinerary?.customers?.length || 0} customer stops
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <SaveScheduleButton
                        scheduleId={selectedSchedule.cluster}
                        isSaved={selectedSchedule.is_saved}
                        onToggleSave={toggleSaveSchedule}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleViewDetails}
                      >
                        <Route className="h-4 w-4 mr-2" />
                        View Details
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="space-y-4">
                      {/* Schedule Stats */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <Text>Created: {formatDate(selectedSchedule.created_at)}</Text>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <Text>Region: {selectedSchedule.metadata?.region || "N/A"}</Text>
                        </div>
                      </div>
                      
                      {/* Progress */}
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Progress</span>
                          <span>{calculateProgress(selectedSchedule)}%</span>
                        </div>
                        <Progress value={calculateProgress(selectedSchedule)} className="h-2" />
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="bg-muted/30 p-4 border-t">
                    <div className="w-full flex flex-col md:flex-row justify-between items-center gap-2 md:gap-0">
                      <Text className="text-sm text-muted-foreground">
                        {selectedSchedule.assigned_to ? (
                          <span className="flex items-center gap-1.5">
                            <UserCheck className="h-4 w-4 text-green-600" />
                            Assigned to: {selectedSchedule.assigned_to.full_name}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5">
                            <UserX className="h-4 w-4 text-muted-foreground" />
                            Unassigned
                          </span>
                        )}
                      </Text>
                      <div className="flex gap-2 mt-2 md:mt-0">
                        <Button variant="secondary" size="sm" onClick={() => setAssignModalOpen(true)}>
                          Assign RSM
                        </Button>
                        <Button variant="outline" size="sm" onClick={handleOpenGoogleMaps}>
                          Google Maps
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={handleViewDetails}
                        >
                          View Full Schedule
                        </Button>
                      </div>
                      <AssignRsmModal
                        isOpen={assignModalOpen}
                        onClose={() => setAssignModalOpen(false)}
                        clusterId={selectedSchedule.cluster}
                        clusterName={selectedSchedule.metadata?.itinerary_name}
                        onAssignComplete={fetchSavedSchedules}
                      />
                    </div>
                  </CardFooter>
                </Card>
              </div>
            ) : (
              <Card>
                <CardContent className="p-6 text-center">
                  <Bookmark className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                  <Text className="text-lg font-medium">No Schedule Selected</Text>
                  <Text className="text-muted-foreground mt-1">
                    {savedSchedules && savedSchedules.length > 0 
                      ? "Select a schedule from the list to view its details" 
                      : "You don't have any saved schedules yet"}
                  </Text>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SavedSchedulesPage;